
import React from 'react';
import { StandardizedConnection } from '@/types/connections';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getConnectorFriendlyName } from '@/utils/connectorNames';
import { plaidConnectionNeedsReauth } from '@/utils/plaidConnectionStatus';
import { toast } from 'sonner';

interface LinkedConnectorItemProps {
  connector: StandardizedConnection;
  onRemove?: (connectorId: string, connectionId: string) => void;
  onConnectorUpdate?: (connectors: StandardizedConnection[]) => void;
  onPlaidReauth?: (connector: StandardizedConnection) => void;
}

const LinkedConnectorItem: React.FC<LinkedConnectorItemProps> = ({ connector, onRemove, onConnectorUpdate, onPlaidReauth }) => {
  const renderConnectorAvatar = (connector: StandardizedConnection) => {
    const isCsvConnection = connector.service === 'csv';
    
    if (isCsvConnection) {
      return (
        <Avatar className="w-10 h-10 mr-4">
          <AvatarFallback className="bg-emerald-100 text-emerald-600">
            CSV
          </AvatarFallback>
        </Avatar>
      );
    }
    
    return (
      <Avatar className="w-10 h-10 mr-4">
        <AvatarImage 
          src={connector.logoUrl}
          alt={connector.name}
        />
        <AvatarFallback>{connector.name.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
    );
  };

  const displayName = (() => {
    if (connector.service === 'csv' && connector.csvImports && Array.isArray(connector.csvImports) && connector.csvImports.length > 0) {
      if (connector.csvImports.length === 1) {
        return connector.csvImports[0].fileName;
      }
      return `${connector.csvImports.length} CSV files`;
    }
    // Use friendly name for non-CSV connectors
    const rawName = connector.metadata?.file_name || connector.name;
    
    // If this looks like a connector type (contains underscores), get friendly name
    if (rawName && rawName.includes('_') && connector.service !== 'csv') {
      return getConnectorFriendlyName(rawName);
    }
    
    return rawName;
  })();
  
  const description = connector.service === 'csv' 
    && (() => {
        if (connector.csvImports && Array.isArray(connector.csvImports) && connector.csvImports.length > 0) {
          if (connector.csvImports.length === 1) {
            return `CSV file: ${connector.csvImports[0].fileName}`;
          }
          const fileNames = connector.csvImports.map(imp => imp.fileName).join(', ');
          return `${connector.csvImports.length} CSV files: ${fileNames}`;
        }
        return `CSV file: ${connector.metadata?.file_name || connector?.file_name || "Unnamed"}`;
      })()

  const connectionNeedsReconnect = (connector: StandardizedConnection) =>
    plaidConnectionNeedsReauth(connector) ||
    connector.needs_reconnect ||
    (connector.metadata && connector.metadata.needs_reconnect) ||
    (connector.metadata?.originalItem?.status?.setup_state === 'broken') ||
    (connector.metadata?.originalItem?.status?.setup_state === 'needs_reauth') ||
    (connector.metadata?.status?.setup_state === 'broken') ||
    (connector.metadata?.status?.setup_state === 'needs_reauth');

  // Determine status display text and class
  const getStatusDisplay = (status: string, connector: StandardizedConnection) => {
    const needsReconnect = connectionNeedsReconnect(connector);
    
    if (needsReconnect) {
      return {
        text: 'Needs Reauthorization',
        className: 'bg-orange-100 text-orange-800'
      };
    } else if (status === 'connected' || status === 'active') {
      return {
        text: 'Connected',
        className: 'bg-green-100 text-green-800'
      };
    } else if (status === 'broken' || status === 'error') {
      return {
        text: 'Error',
        className: 'bg-red-100 text-red-800'
      };
    } else {
      return {
        text: 'Incomplete',
        className: 'bg-yellow-100 text-yellow-800'
      };
    }
  };

  const statusDisplay = getStatusDisplay(connector.status, connector);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(connector.id, connector?.connectionId);
    }
  };

  const handleReconnect = async () => {
    const reconnectUrl = connector.reconnect_url || 
                        (connector.metadata && connector.metadata.reconnect_url);
    
    console.log(`[handleReconnect] connectorId=${connector.id} connectionId=${connector.connectionId} reconnect_url=${reconnectUrl}`);
    
    if (!reconnectUrl) {
      console.error('[handleReconnect] No reconnect URL available for connector:', connector.id);
      toast.error('Unable to reconnect - no URL available');
      return;
    }

    try {
      // Import the form token manager (same as handleConnect)
      const { saveFormDataWithToken } = await import('@/utils/formTokenManager');
      
      // Store reconnect state in sessionStorage (same as handleConnect)
      sessionStorage.setItem('connectorRedirect', 'true');
      sessionStorage.setItem('isReconnect', 'true');
      sessionStorage.setItem('selectedConnectorId', connector.connectorId);
      sessionStorage.setItem('selectedConnectionId', connector.connectionId);

      // Save current route step before redirecting
      const currentPath = window.location.pathname;
      const stepMatch = currentPath.match(/\/step\/(\d+)/);
      if (stepMatch && stepMatch[1]) {
        sessionStorage.setItem('savedFormStep', stepMatch[1]);
      }
      
      // Save form data to server and get token
      const token = await saveFormDataWithToken();
      
      if (!token) {
        toast.error('Failed to save form data. Please try again.');
        return;
      }

      // Construct redirect URI with token (same as handleConnect)
      const baseRedirectUri = window.location.href;
      const redirectUri = new URL(baseRedirectUri);
      redirectUri.searchParams.set('form_token', token);
      
      // Add redirect_uri parameter to reconnect URL
      const finalReconnectUrl = new URL(reconnectUrl);
      finalReconnectUrl.searchParams.set('redirect_uri', redirectUri.toString());
      
      console.log(`[handleReconnect] redirectUri=${redirectUri.toString()}`);
      console.log(`[handleReconnect] finalReconnectUrl=${finalReconnectUrl.toString()}`);
      
      // Redirect to Fivetran Connect Card with token in redirect URI
      window.location.href = finalReconnectUrl.toString();
    } catch (error) {
      console.error('Error during reconnect:', error);
      toast.error('Failed to initiate reconnection');
    }
  };

  const needsReconnect = connectionNeedsReconnect(connector);

  return (
    <div className="border rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center">
        {renderConnectorAvatar(connector)}
        <div>
          <h4 className="font-medium">{displayName}</h4>
          <p className="text-sm text-gray-500">{description}</p>
          <span className={`${statusDisplay.className} px-3 py-1 rounded-full text-xs mt-1 inline-block`}>
            {statusDisplay.text}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {needsReconnect && connector.service === 'plaid' && onPlaidReauth && (
          <button
            className="text-orange-600 hover:text-orange-800 border border-orange-300 hover:border-orange-400 rounded-full px-4 py-1 bg-orange-50 hover:bg-orange-100 transition-colors text-sm"
            onClick={() => onPlaidReauth(connector)}
            aria-label="Re-authenticate bank connection"
          >
            Re-authenticate
          </button>
        )}
        {needsReconnect && connector.service !== 'plaid' && (
          <button
            className="text-blue-600 hover:text-blue-800 border border-blue-300 hover:border-blue-400 rounded-full px-4 py-1 bg-blue-50 hover:bg-blue-100 transition-colors"
            onClick={handleReconnect}
            aria-label="Reconnect to Fivetran"
          >
            Reconnect
          </button>
        )}
        <button
          className="text-gray-500 hover:text-red-500 border border-gray-300 hover:border-red-200 rounded-full px-4 py-1"
          data-testid={`remove-connector-${connector.id}`}
          onClick={handleRemove}
          aria-label="Remove connector"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default LinkedConnectorItem;
